import React, { useEffect, useMemo, useRef } from 'react'
import { useWatch } from 'react-hook-form'
import Box from '@mui/material/Box'
import Warning from '@mui/icons-material/Warning'
import Typography from '@/v2/components/common/Typography'
import SelectInputField from '@/v2/components/common/SelectInputField'
import Row from '@/v2/components/common/Row'
import { range, geThisDayOnNextMonth } from '@/v2/utils/helpers'
import { toUSNumberFormat } from '@/v2/utils/format'
import {
  currencyToNumber,
  calculateAmountWithDiscount,
  calculateTotalDue,
  calculateMonthlyPayments,
  validateInstallmentPayment,
  calculateDefaultInitialPayment,
} from '@/v2/utils/paymentCalculations'
import { InitialPaymentField } from '../InitialPaymentField'

const MIN_INSTALLMENTS = 2
const MAX_INSTALLMENTS = 12
const INSTALLMENT_STEP = 1

const InstallmentFields = ({
  register,
  errors,
  control,
  setValue,
  getFieldState,
}) => {
  const previousInstallmentsRef = useRef(null)
  const previousAmountRef = useRef(null)
  const downPaymentManuallyModifiedRef = useRef(false)
  const previousDownPaymentRef = useRef(null)
  const isProgrammaticUpdateRef = useRef(false)

  const watchedValues = useWatch({
    control,
    name: [
      'classification',
      'amount',
      'discountRate',
      'interestRate',
      'installments',
      'downPayment',
    ],
  })

  const defaults = control._defaultValues || {}

  const [
    classification = defaults.classification || '',
    amount = defaults.amount || '',
    discountRate = defaults.discountRate || 0,
    interestRate = defaults.interestRate || 0,
    installments = defaults.installments || 1,
    downPayment = defaults.downPayment || '',
  ] = watchedValues

  useEffect(() => {
    if (
      previousDownPaymentRef.current !== null &&
      previousDownPaymentRef.current !== downPayment
    ) {
      if (!isProgrammaticUpdateRef.current) {
        downPaymentManuallyModifiedRef.current = true
      }
      isProgrammaticUpdateRef.current = false
    }
    previousDownPaymentRef.current = downPayment
  }, [downPayment])

  const calculations = useMemo(() => {
    if (!amount) {
      return {
        amountWithDiscount: 0,
        totalDue: 0,
        monthlyPayment: '0',
        monthlyPaymentWithoutDiscount: '0',
        initialPayment: 0,
        validation: { isValid: true, error: null },
      }
    }

    const amountWithDiscount = calculateAmountWithDiscount(amount, discountRate)
    const totalDue = calculateTotalDue(amountWithDiscount, interestRate)
    const monthlyPayment = calculateMonthlyPayments(
      totalDue,
      downPayment,
      installments || MIN_INSTALLMENTS
    )

    // Calculate monthly payment without discount (but with interest)
    const numericAmount = currencyToNumber(amount)
    const totalDueWithoutDiscount = calculateTotalDue(
      numericAmount,
      interestRate
    )
    const monthlyPaymentWithoutDiscount = calculateMonthlyPayments(
      totalDueWithoutDiscount,
      downPayment,
      installments || MIN_INSTALLMENTS
    )

    const initialPayment = currencyToNumber(downPayment)
    const validation = validateInstallmentPayment(totalDue, downPayment)

    return {
      amountWithDiscount,
      totalDue,
      monthlyPayment,
      monthlyPaymentWithoutDiscount,
      initialPayment,
      validation,
    }
  }, [amount, discountRate, interestRate, installments, downPayment])

  useEffect(() => {
    if (!amount || !installments) return

    const setValueOptions = {
      shouldValidate: false,
      shouldDirty: false,
      shouldTouch: false,
    }

    setValue(
      'amountWithDiscount',
      calculations.amountWithDiscount,
      setValueOptions
    )
    setValue('totalDue', calculations.totalDue, setValueOptions)
    setValue('installmentAmount', calculations.monthlyPayment, setValueOptions)
    setValue(
      'installmentAmountWithoutDiscount',
      calculations.monthlyPaymentWithoutDiscount,
      setValueOptions
    )
  }, [
    amount,
    installments,
    downPayment,
    discountRate,
    interestRate,
    calculations,
    setValue,
  ])

  useEffect(() => {
    if (classification === 'installments' && installments < MIN_INSTALLMENTS) {
      setValue('installments', MIN_INSTALLMENTS)
    }
  }, [classification, installments, setValue])

  useEffect(() => {
    if (classification === 'installments') {
      downPaymentManuallyModifiedRef.current = false
    }
  }, [classification])

  useEffect(() => {
    if (
      classification === 'installments' &&
      amount &&
      installments &&
      previousInstallmentsRef.current !== null &&
      previousInstallmentsRef.current !== installments &&
      !downPaymentManuallyModifiedRef.current
    ) {
      const defaultDownPayment = calculateDefaultInitialPayment(
        amount,
        installments,
        discountRate,
        interestRate
      )
      isProgrammaticUpdateRef.current = true
      setValue('downPayment', defaultDownPayment, { shouldDirty: false })
    }
    previousInstallmentsRef.current = installments
  }, [
    classification,
    installments,
    amount,
    discountRate,
    interestRate,
    setValue,
  ])

  useEffect(() => {
    if (
      classification === 'installments' &&
      amount &&
      installments &&
      previousAmountRef.current !== null &&
      previousAmountRef.current !== amount &&
      !downPaymentManuallyModifiedRef.current
    ) {
      const defaultDownPayment = calculateDefaultInitialPayment(
        amount,
        installments,
        discountRate,
        interestRate
      )
      isProgrammaticUpdateRef.current = true
      setValue('downPayment', defaultDownPayment, { shouldDirty: false })
    }
    previousAmountRef.current = amount
  }, [
    classification,
    amount,
    installments,
    discountRate,
    interestRate,
    setValue,
  ])

  if (classification !== 'installments') return null

  const { initialPayment, monthlyPayment, validation } = calculations
  const shouldShowSummary = installments > 1 && amount

  const remainingMonthlyPayments =
    initialPayment > 0 ? installments - 1 : installments

  return (
    <>
      <Row>
        <SelectInputField
          noMargin
          label="Installments"
          name="installments"
          id="payment-plan-installments"
          options={range(
            MIN_INSTALLMENTS,
            MAX_INSTALLMENTS,
            INSTALLMENT_STEP
          ).map(num => ({
            label: num,
            value: num,
          }))}
          color="secondary"
          error={errors?.installments?.message}
          {...register('installments', { valueAsNumber: true })}
        />

        <InitialPaymentField
          control={control}
          errors={errors}
          info="Increasing initial payment will lower monthly installment payments."
        />
      </Row>

      {shouldShowSummary && (
        <Box
          bgcolor={validation.isValid ? 'gray.light' : 'error.main'}
          borderRadius={2}
          px={2}
          py={2}
          mb={2}
        >
          <Box display="flex" flexDirection="column" gap={1}>
            {validation.isValid ? (
              <>
                <Box display="flex" gap={1}>
                  <Typography variant="body2">
                    Total due today (Initial Payment):
                  </Typography>
                  <Typography variant="body2" bold>
                    {toUSNumberFormat(initialPayment, true, true)}
                  </Typography>
                </Box>

                {monthlyPayment > 0 && remainingMonthlyPayments > 0 && (
                  <>
                    <Box display="flex" gap={1}>
                      <Typography variant="body2">
                        {remainingMonthlyPayments}{' '}
                        {remainingMonthlyPayments === 1
                          ? 'payment'
                          : 'payments'}{' '}
                        of {toUSNumberFormat(monthlyPayment, true, true)}
                      </Typography>
                    </Box>

                    <Box display="flex" gap={1}>
                      <Typography
                        fontWeight="light"
                        variant="body3"
                        color="fieldLabel.main"
                      >
                        Next payment {geThisDayOnNextMonth()}
                      </Typography>
                    </Box>
                  </>
                )}
              </>
            ) : (
              <Typography
                variant="body2"
                color="white.main"
                fontWeight="light"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <Warning />
                {validation.error}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </>
  )
}

export default InstallmentFields
